/**
 * Agent Configuration
 * 
 * Maps Clerk organizations to their Retell agent IDs.
 * Each organization has its own isolated set of agents.
 */

export interface AgentConfig {
  name: string;
  agentType: 'welcome' | 'settlement';
  retellAgentIds: string[];
  assemblyAiPromptType: string;
}

export interface OrganizationConfig {
  orgSlugPrefix: string;
  displayName: string;
  agents: AgentConfig[];
}

/**
 * Organization to Agent mapping
 * 
 * - DigniFi: Welcome Agent for FPD outreach
 * - Nationwide Capital: Settlement Agent (Angela SIF) for debt settlement
 */
export const ORGANIZATION_AGENTS: OrganizationConfig[] = [
  {
    orgSlugPrefix: 'dignifi',
    displayName: 'DigniFi',
    agents: [
      {
        name: 'Welcome Agent',
        agentType: 'welcome',
        retellAgentIds: [
          'agent_fa15c9a62a0747b63531f254cc', // Outbound Welcome Agent
          'agent_72cb2989c765418d56cff35725', // Inbound Welcome Agent (callbacks)
        ],
        assemblyAiPromptType: 'welcome_agent',
      },
    ],
  },
  {
    orgSlugPrefix: 'nationwide-capital',
    displayName: 'Nationwide Capital',
    agents: [
      {
        name: 'Angela SIF Agent',
        agentType: 'settlement',
        retellAgentIds: [
          'agent_f15066072e3c121150b6adc18b', // Inbound
          // Outbound agent ID will be added here when available
        ],
        assemblyAiPromptType: 'settlement_agent',
      },
    ],
  },
];

/**
 * Get agent config for an organization based on slug prefix
 */
export function getAgentConfigForOrg(orgSlug: string | null): OrganizationConfig | null {
  if (!orgSlug) return null;
  
  const normalizedSlug = orgSlug.toLowerCase();
  return ORGANIZATION_AGENTS.find(org => 
    normalizedSlug.startsWith(org.orgSlugPrefix)
  ) || null;
}

/**
 * Get all Retell agent IDs for an organization
 */
export function getRetellAgentIdsForOrg(orgSlug: string | null): string[] {
  const orgConfig = getAgentConfigForOrg(orgSlug);
  if (!orgConfig) return [];
  
  return orgConfig.agents.flatMap(agent => agent.retellAgentIds);
}

/**
 * Get agent type for an organization
 */
export function getAgentTypeForOrg(orgSlug: string | null): 'welcome' | 'settlement' | null {
  const orgConfig = getAgentConfigForOrg(orgSlug);
  if (!orgConfig || orgConfig.agents.length === 0) return null;
  
  return orgConfig.agents[0].agentType;
}
